<template>
  <div class="dropdown-w datepicker-w">
    <b-tooltip position="is-bottom" multilined type="is-dark">
      <div class="datepicker-w-i">
        <date-picker v-model="phonesFilterDates" range :shortcuts="datePickerShortcuts" format="DD/MM/YYYY" range-separator=" - " :append-to-body="false" :lang="lang"
              :placeholder="$t('Datepicker.AllTime')"
              @change="onDatePeriodChange" @open="onOpen">
          <template #icon-calendar>
            <icon-base width="18" height="19" view-box="0 0 18 19" icon-name="calendar"><icon-calendar /></icon-base>
          </template>
        </date-picker>
      </div>
      <template v-slot:content>
        <p>{{ $t("Phones.Tooltips.Filters.Date") }}</p>
      </template>
    </b-tooltip>
  </div>
</template>

<script lang="ts">
import moment from "moment";
import Fragment from "vue-fragment";
import { Component, Vue, Watch } from "vue-property-decorator";
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import "vue2-datepicker/locale/he";
import { Action, Getter } from "vuex-class";
import HelperService from "@/services/HelperService";
import IconBase from "../IconBase.vue";
import IconCalendar from "../icons/IconCalendar.vue";

Vue.use(Fragment.Plugin);
const namespace = "phonesModule";

@Component({
  components: {
    IconBase,
    IconCalendar,
    DatePicker,
  },
})
export default class PhonesFiltersDate extends Vue {
  @Getter("datePeriod", { namespace }) datePeriod!: Date[];
  @Action("setDatePeriod", { namespace }) setDatePeriod!: (datePeriod: (Date | undefined)[]) => void;

  lang = "he";

  public phonesFilterDates?: (Date | undefined)[] = HelperService.getThisMonthPeriod();

  @Watch("datePeriod", { immediate: true })
  onDatePeriodChangeWatcher(newDatePeriod: Date[], oldDatePeriod: Date[]): void {
    if (oldDatePeriod === undefined || newDatePeriod === undefined || oldDatePeriod.length === 0
      || moment(newDatePeriod[0]).format("YYYY-MM-DD") === moment(oldDatePeriod[0]).format("YYYY-MM-DD")
      && moment(newDatePeriod[1]).format("YYYY-MM-DD") === moment(oldDatePeriod[1]).format("YYYY-MM-DD")) {
      return;
    } else {
      this.phonesFilterDates = this.datePeriod;
    }
  }

  @Watch("$i18n.locale", { immediate: true })
  localeChanged(newLocale: string, oldLocale: string): void {
    if (newLocale === oldLocale) {
      return;
    }
    this.lang = HelperService.getLanguageCodeWithoutCountry(newLocale);
  }
  
  get datePickerShortcuts(): any[] {
    return HelperService.getDatePickerShortcuts();
  }

  beforeMount(): void {
    if (!this.datePeriod || this.datePeriod.length === 0) {
      this.phonesFilterDates = HelperService.getThisMonthPeriod();
    } else {
      this.phonesFilterDates = this.convertStringToDate(this.datePeriod);
    }
    this.setDatePeriod(this.phonesFilterDates);
  }

  convertStringToDate(dates: Date[] | string[]): Date[] {
    if (typeof dates[0] === "string") {
      dates[0] = new Date(dates[0]);
    }
    if (dates.length > 1 && typeof dates[1] === "string") {
      dates[1] = new Date(dates[1]);
    }
    return dates as Date[];
  }

  onDatePeriodChange(datePeriod: Date[]): void {
    this.setDatePeriod(datePeriod);
  }

  onOpen(event: MouseEvent) {
    // if no space to the right of the page, popup will move to the left, so we need to draw arrow at another side
    setTimeout(() => {
      const popup = (document.getElementsByClassName("mx-datepicker-popup"))[0] as HTMLDivElement;
      if (popup.style.left !== "0px") {
        popup.classList.add("leftoffset");
      }
    });
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables";

.dropdown-w {
  padding: 0 12px 20px;
  &.datepicker-w {
    width: 25%;
    flex: 0 0 25%;
    @media (max-width: 1599px) {
      width: 33.3333%;
      flex: 0 0 33.3333%;
      @media (resolution: 120dpi) {
        width: 25%;
        flex: 0 0 25%;
      }
    }
    @media (max-width: 1279px) and (resolution: 120dpi) {
      width: 33.3333%;
      flex: 0 0 33.3333%;
    }
    @media (max-width: 991px) {
      width: 100%;
      flex: 1;
      padding: 0 8px 15px;
    }
    .datepicker-w-i {
      position: relative;
    }
    .dropdown {
      width: 100%;
      .dropdown-trigger {
        width: 100%;
        .control {
          width: 100%;
        }
      }
    }
  }
  .b-tooltip {
    width: 100%;
  }
}
</style>

<style lang="scss">
@import "@/assets/styles/variables";

.section-filters {
  .dropdown-w {
    &.datepicker-w {
      .dropdown {
        .dropdown-trigger {
          .control {
            width: 100%;
          }
        }
      }
    }
  }
}

.mx-datepicker {
}

.mx-datepicker-range {
  width: 100%;
}

.mx-input {
  padding: 6px 39px 6px 13px;
  border-color: #d3d6e2;
  font-size: 17px;
  box-shadow: none;
  color: $dark;
  height: 39px;
  @media (max-width: 991px) {
    height: 35px;
    font-size: 16px;
    padding: 5px 39px 5px 10px;
  }
  &:hover,
  &:focus {
    border-color: #d3d6e2;
  }
}

.mx-icon-calendar {
  .icon-w {
    color: $primary;
  }
}
.mx-datepicker svg {
  width: inherit;
  height: inherit;
}

.mx-datepicker-main {
  font-family: "Assistant", sans-serif;
  color: $dark;
}

.mx-datepicker-popup {
  border: 1px solid #d3d6e2;
  border-radius: 4px;
  box-shadow: 0px 2px 4px rgba(143, 146, 155, 0.3);
  margin: 10px 0;
  width: 660px;

  @media (max-width: 991px) {
    width: 256px;
  }

  &:before {
    content: "";
    display: block;
    position: absolute;
    left: 34px;
    top: -13px;
    border-width: 7px 6px;
    border-style: solid;
    border-color: transparent transparent #fff;
    z-index: 1;
    @media (max-width: 991px) {
      display: none;
    }
  }

  &:after {
    content: "";
    display: block;
    position: absolute;
    left: 33px;
    top: -16px;
    border-width: 8px 7px;
    border-style: solid;
    border-color: transparent transparent #d3d6e2;
    @media (max-width: 991px) {
      display: none;
    }
  }

   &.leftoffset {
    &:before {
      left: auto;
      right: 34px;
    }

    &:after {
      left: auto;
      right: 33px;
    }
  }
}

.mx-datepicker-sidebar {
  width: 153px;
  padding: 15px 0 15px 15px;
  @media (max-width: 991px) {
    display: none;
    // float: none;
    // width: 100%;
    // padding: 15px;
  }
}
.mx-btn-shortcut {
  width: 100%;
  background: #f6f7fb;
  border-radius: 4px;
  color: $dark;
  font-size: 14px;
  font-weight: 600;
  text-align: right;
  padding: 3px 8px;
  line-height: 20px;
  transition: all 0.2s;
  &:not(:last-child) {
    margin-bottom: 9px;
  }
  &:hover {
    background: $primary;
    color: #fff;
  }
}
.mx-datepicker-sidebar + .mx-datepicker-content {
  margin-left: 153px;
  border: none;
  @media (max-width: 991px) {
    margin: 0;
  }
}
.mx-datepicker-content {
  padding: 15px;
}

.mx-calendar {
  padding: 0;
  width: 224px;
  + .mx-calendar {
    margin-right: 15px;
    border: none;
    @media (max-width: 991px) {
      // margin: 15px 0 0 0;
      display: none;
    }
  }
}

.mx-btn-icon-double-left,
.mx-btn-icon-double-right {
  display: none;
}
.mx-icon-left {
  &:before {
    background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 7 12"><path fill="%23263238" d="M0,1.41,4.33,6,0,10.59,1.33,12,7,6,1.33,0Z"/></svg>') 50% 50%
      no-repeat;
    width: 7px;
    height: 14px;
    border: none;
    transform: none;
  }
}
.mx-icon-right {
  &:before {
    background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 7 12"><path fill="%23263238" d="M7,10.59,2.67,6,7,1.41,5.67,0,0,6l5.67,6Z"/></svg>') 50% 50% no-repeat;

    width: 7px;
    height: 14px;
    border: none;
    transform: none;
  }
}
.mx-btn-icon-left {
  float: right;
}
.mx-btn-icon-right {
  float: left;
}

// .mx-calendar:nth-child(1) {
//   .mx-btn-icon-right {
//     visibility: hidden;
//     opacity: 0;
//   }
// }
// .mx-calendar:nth-child(2) {
//   .mx-btn-icon-left {
//     visibility: hidden;
//     opacity: 0;
//   }
// }

.mx-calendar-header-label {
  .mx-btn {
    font-size: 16px;
    font-weight: 600;
    color: $dark;
  }
}
.mx-calendar-content {
  height: auto;
  .cell {
    > div {
      border-radius: 4px;
    }
    &:hover {
      // color: $primary;
      background: #f6f7fb;
    }
    &.active {
      background: none;
      > div {
        background: $primary;
      }
    }
    &:not(.not-current-month) {
      &.in-range,
      &.hover-in-range {
        background: #ddf5ff;
      }
    }
  }
  .today {
    color: $primary;
  }
}
.mx-table th {
  font-weight: 600;
  color: $dark;
}
.mx-table-date td,
.mx-table-date th {
  height: 24px;
  font-size: 15px;
}

.mx-table-date .cell.not-current-month {
  color: #aaa;
}
</style>
